"use client"

import { useCallback, useEffect, useState } from "react"

export interface AnalysisBreadcrumb {
  savedAnalysisId: string
  grassType: string
  zipCode: string
  savedAt: number
}

const STORAGE_KEY = "lastAnalysis_breadcrumb"
const TTL_MS = 60 * 60 * 1000

export function readBreadcrumb(): AnalysisBreadcrumb | null {
  if (typeof window === "undefined") return null
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw) as Partial<AnalysisBreadcrumb>
    if (
      !parsed ||
      typeof parsed.savedAnalysisId !== "string" ||
      typeof parsed.grassType !== "string" ||
      typeof parsed.savedAt !== "number"
    ) {
      return null
    }
    if (Date.now() - parsed.savedAt > TTL_MS) {
      window.localStorage.removeItem(STORAGE_KEY)
      return null
    }
    return {
      savedAnalysisId: parsed.savedAnalysisId,
      grassType: parsed.grassType,
      zipCode: typeof parsed.zipCode === "string" ? parsed.zipCode : "",
      savedAt: parsed.savedAt,
    }
  } catch {
    return null
  }
}

export function writeBreadcrumb(breadcrumb: AnalysisBreadcrumb): void {
  if (typeof window === "undefined") return
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(breadcrumb))
  } catch {
    // localStorage unavailable (private browsing, quota) — best-effort
  }
}

export function clearBreadcrumb(): void {
  if (typeof window === "undefined") return
  try {
    window.localStorage.removeItem(STORAGE_KEY)
  } catch {
    // ignore
  }
}

export function useLastAnalysisBreadcrumb() {
  const [breadcrumb, setBreadcrumb] = useState<AnalysisBreadcrumb | null>(null)

  useEffect(() => {
    setBreadcrumb(readBreadcrumb())
  }, [])

  const clear = useCallback(() => {
    clearBreadcrumb()
    setBreadcrumb(null)
  }, [])

  return { breadcrumb, clear }
}
